import {
  CascadiaColor,
  CascadiaSpacing,
  CascadiaTypographyStyle,
} from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import React, { AriaAttributes, ElementType } from "react";

import { getTextStyle } from "./styles";

import "./Text.css";

export interface TextProps {
  id?: string;
  style?: CascadiaTypographyStyle;
  children: React.ReactNode;
  className?: string;
  align?: "start" | "center" | "end";
  as?: ElementType;
  color?: CascadiaColor;
  tabIndex?: string | number;
  dataKey?: string;
  testID?: string;
  marginTop?: CascadiaSpacing;
  truncate?: boolean;
  ["aria-live"]?: AriaAttributes["aria-live"];
}

function getDefaultStyle(component: ElementType) {
  switch (component) {
    case "h1":
      return "global/heading-large";
    case "h2":
      return "form/page-title";
    case "h3":
      return "global/body-secondary";
    case "h4":
      return "input/text-field";
    default:
      return "global/body-secondary";
  }
}

export function Text(props: TextProps) {
  const {
    id,
    as: Component = "span",
    style = getDefaultStyle(Component),
    children,
    className,
    align,
    color = "text-base",
    tabIndex,
    dataKey,
    testID = "text",
    marginTop,
    truncate,
    ...rest
  } = props;

  return (
    <Component
      data-key={dataKey}
      tabIndex={tabIndex}
      id={id}
      aria-live={rest["aria-live"]}
      className={css([
        {
          color: `var(--${color})`,
          margin: "var(--space-0)",
          padding: "var(--space-0)",
          textAlign: align,
        },
        marginTop && { marginTop: `var(--${marginTop})` },
        getTextStyle(style),
        truncate && {
          overflow: "hidden",
          display: "inline-block",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
        className,
      ])}
      title={truncate && typeof children === "string" ? children : ""}
      data-testid={testID}
    >
      {children}
    </Component>
  );
}
